/**
 * The estimator's motion tokens. Enters ease out, exits ease in and run about 30% faster, and
 * only opacity and transform animate. Under reduced motion, components fade only.
 */

/** Durations in seconds. */
export const DURATION = { fast: 0.15, base: 0.22, slow: 0.32 } as const;

/** How much faster an exit runs than its enter. */
const EXIT_FACTOR = 0.7;

/** App easing from the style guide, for anything entering. */
export const EASE_OUT = [0.22, 1, 0.36, 1] as const;

/** For anything leaving. */
export const EASE_IN = [0.4, 0, 1, 1] as const;

/**
 * An exit transition matching an enter of `duration`: a little quicker, easing in.
 * @param duration - The enter's duration, in seconds.
 * @returns The exit transition.
 * @throws Never.
 */
export const exitAfter = (duration: number) => ({ duration: duration * EXIT_FACTOR, ease: EASE_IN });

/** Section and row enter: fade up 8px. */
export const ENTER = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4, transition: exitAfter(DURATION.base) },
  transition: { duration: DURATION.base, ease: EASE_OUT },
} as const;

/** Popover open: 150ms from 96% and a fade, from its anchor (Radix sets the transform origin). */
export const POPOVER_MOTION = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96, transition: exitAfter(DURATION.fast) },
  transition: { duration: DURATION.fast, ease: EASE_OUT },
} as const;

/** Chips and table columns entering or leaving. */
export const POP_IN = {
  initial: { opacity: 0, scale: 0.96 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96, transition: exitAfter(DURATION.base) },
  transition: { duration: DURATION.base, ease: EASE_OUT },
} as const;

/** Under reduced motion: the same timing, opacity only. */
export const FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: exitAfter(DURATION.fast) },
  transition: { duration: DURATION.fast },
} as const;

/** Delay between staggered siblings, in seconds. */
export const STAGGER_S = 0.04;

/** Longest stagger delay, in seconds, so a long list is in place within a quarter second. */
export const MAX_STAGGER_S = 0.25;

/**
 * A list item's stagger delay.
 * @param index - The item's position, from 0.
 * @returns Its delay in seconds, at most {@link MAX_STAGGER_S}.
 * @throws Never.
 */
export const staggerDelay = (index: number) => Math.min(index * STAGGER_S, MAX_STAGGER_S);

/**
 * Classes for a surface's root (and each portaled popover): every enabled button presses in to
 * 98% at once, and lucide icons draw at a 1.75 stroke unless one sets its own weight.
 */
export const SURFACE_FEEL =
  "[&_button:not(:disabled):active]:scale-[0.98] [&_svg.lucide:not([stroke-width='2.5'])]:stroke-[1.75]";
